/**
 * Embed querystring contract (M6). One pure module both sides bind to: the
 * embed BUILDER (share modal) serializes its toggles with `buildEmbedQuery`,
 * and the /embed/{slug} page parses the incoming search params with
 * `parseEmbedQuery` — so a toggle flipped in the builder and a param typed by
 * hand mean exactly the same thing. Format (documented in M6.md):
 *
 *   ?controls=zoom,filter,search,fs   which chrome renders ("none" = bare map)
 *   &legend=1|0                       force the legend on/off (absent → the
 *                                     published MapSpec's own legend setting)
 *   &panlock=1                        freeze the viewport (no pan/zoom input)
 *   &mode=store_locator               the store-locator embed preset
 *   &w=100%&h=480                     echo of the iframe size (layout is the
 *                                     iframe's job — the page just fills it)
 */

export type EmbedControl = "zoom" | "filter" | "search" | "fs";

export interface EmbedOptions {
  /** Zoom in/out column. */
  zoom: boolean;
  /** Quick filter chip over the published color-by column. */
  filter: boolean;
  /** Find-a-place search box. */
  search: boolean;
  /** Fullscreen button. */
  fullscreen: boolean;
  /** Tri-state: null follows the published spec's legend setting. */
  legend: boolean | null;
  /** Freeze the viewport — no pan/zoom interaction at all. */
  panLock: boolean;
  mode: "standard" | "store_locator";
}

/** Bare-URL default: zoom only, spec-owned legend, interactive, standard. */
export const DEFAULT_EMBED_OPTIONS: EmbedOptions = {
  zoom: true,
  filter: false,
  search: false,
  fullscreen: false,
  legend: null,
  panLock: false,
  mode: "standard",
};

export interface EmbedSize {
  width: number | "100%";
  height: number;
}

type SearchParamValue = string | string[] | undefined;

function first(value: SearchParamValue): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

/** Parse the /embed/{slug} search params into options (unknown values ignored). */
export function parseEmbedQuery(params: Record<string, SearchParamValue>): EmbedOptions {
  const options: EmbedOptions = { ...DEFAULT_EMBED_OPTIONS };

  const controls = first(params.controls);
  if (controls !== undefined) {
    const list = controls.split(",").map((c) => c.trim().toLowerCase());
    options.zoom = list.includes("zoom");
    options.filter = list.includes("filter");
    options.search = list.includes("search");
    options.fullscreen = list.includes("fs") || list.includes("fullscreen");
  }

  const legend = first(params.legend);
  if (legend === "1") options.legend = true;
  else if (legend === "0") options.legend = false;

  if (first(params.panlock) === "1") options.panLock = true;
  if (first(params.mode) === "store_locator") options.mode = "store_locator";

  return options;
}

/** Serialize options (+ the iframe size echo) into the canonical querystring. */
export function buildEmbedQuery(options: EmbedOptions, size?: EmbedSize): string {
  const parts: string[] = [];

  const controls: EmbedControl[] = [];
  if (options.zoom) controls.push("zoom");
  if (options.filter) controls.push("filter");
  if (options.search) controls.push("search");
  if (options.fullscreen) controls.push("fs");
  parts.push(`controls=${controls.length > 0 ? controls.join(",") : "none"}`);

  if (options.legend !== null) parts.push(`legend=${options.legend ? "1" : "0"}`);
  if (options.panLock) parts.push("panlock=1");
  if (options.mode === "store_locator") parts.push("mode=store_locator");
  if (size) {
    parts.push(`w=${encodeURIComponent(String(size.width))}`);
    parts.push(`h=${size.height}`);
  }

  return parts.join("&");
}
